// --------------------------------------------------------------------------
//  Export builder data layer.
//
//  Turns the dashboard's live data into the building blocks of a composed
//  export. Three responsibilities live here, all pure (no JSX):
//
//    1. COMPONENT_LIBRARY + resolveSection(): the catalogue of selectable
//       data blocks and a resolver that returns the *real* numbers/rows for
//       each one. The same resolved block feeds both the on-screen preview
//       and the generated file, so what the customer sees is what they get.
//    2. PROVIDED_TEMPLATES + custom-template persistence: ready-made layouts
//       plus save / load / delete of the customer's own named layouts.
//    3. LARGE_TABLES + schedule persistence: the full row-level datasets that
//       download on their own, and the monthly-capped delivery schedules.
//
//  Postgres mapping:
//    - section copy / template definitions stay in code (product copy).
//    - custom templates  -> `user_export_templates` (id, user_id, org_id,
//      name, config_json, created_at).
//    - schedules         -> `user_exports_schedule` (id, user_id, org_id,
//      title, template_ref, format, recipients TEXT[], cadence, day_of_month,
//      enabled, next_send_at, created_by). cadence is capped at 'monthly' in
//      the API as well as here so a client can't post a faster schedule.
//    - large-table downloads -> streamed from the canonical tables; the
//      column lists below mirror the report views in data.js.
//
//  Integration hook: replace the localStorage helpers with RTK Query
//  mutations (`useSaveExportTemplateMutation`, `useScheduleExportMutation`)
//  and the resolvers with selectors over the report slices.
// --------------------------------------------------------------------------

import {
    SIMS, COURSES, DEPTS, SEND_LOG, COURSE_SEND_LOG,
    COURSE_USER_COMPLETIONS, PERIOD_DATA, CLICKS_BY_MONTH, courseType,
    mapSimEvent, mapCourseEvent, simStatsFromAgg, PERIOD_DAYS,
} from './data';
import {
    ORG_SCORE, ORG_BAND, BENCHMARK, TOP_RISK_USERS, ORG_FACTOR_STATS,
    SIGNALS, ACTIVE_USERS, bandFor,
} from './riskScore';
import { DWM_SUMMARY, DWM_BY_EMAIL, DWM_TREND } from './dwmData';
// Straight from the shared helper module: importing these from the page
// components pulled React and MUI into the export layer, which stops it
// being reusable by the server-side delivery renderer.
import {
    fmtLocal, STATUS_META, courseScore, courseStatus, courseOutcome,
} from './sendLogFormat';
import { FEATURES, isWidgetEntitled } from './featureFlags';

// -- Small format helpers --------------------------------------------------
const num = (n) => (n == null ? '' : Number(n).toLocaleString('en-GB'));
const pct = (n) => `${Math.round(n)}%`;
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export function escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

export function escapeCsv(value) {
    if (value == null) return '';
    const s = String(value);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function downloadBlob(content, mime, filename) {
    const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function slugify(s) {
    return String(s || 'export').trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/gi, '').toLowerCase() || 'export';
}

// -- Component library -----------------------------------------------------
// Curated per the exports feedback: top-10 caps on ranked tables (full data
// lives in the Data tables page), no per-item drill toggles (they never
// appended real data), and `feature` gates a block behind a tenancy flag so
// it disappears when the feature is off.
// `widgets` names the report widgets a block is drawn from, as
// "<page>:<widgetId>" keys matching the Features page switches. A block is
// offered only while the tenancy is entitled to at least one of them, so the
// export builder reflects the same feature switches the pages do (see
// isSectionEntitled below). Blocks with no mapping are always offered.
export const COMPONENT_LIBRARY = [
    { group: 'Home', items: [
        { id: 'home.risk-dial',    label: 'Risk score & benchmark', widgets: ['summary:risk-dial', 'summary:benchmark-position'] },
        { id: 'home.risk-trend',   label: 'Risk score trend',       widgets: ['summary:risk-trend'] },
        { id: 'home.top-risk',     label: 'High risk users - top 10' },
        { id: 'home.metrics',      label: 'Period metric strip (click rate / completion / engaged)' },
        { id: 'text.implication',  label: 'Implication statements' },
    ]},
    { group: 'Simulations', items: [
        { id: 'sims.metrics',      label: 'Sim metric strip (sends / click / report / 2nd stage)', widgets: ['sims:metric-sends', 'sims:metric-click-rate', 'sims:metric-report-rate', 'sims:metric-second-stage'] },
        { id: 'sims.clicks',       label: 'Clicks by month',              widgets: ['sims:clicks-by-month'] },
        { id: 'sims.funnel',       label: 'Simulation engagement',        widgets: ['sims:sim-engagement'] },
        { id: 'sims.by-sim',       label: 'Top 10 clicked simulations',   widgets: ['sims:click-by-sim'] },
        { id: 'sims.device',       label: 'Clicks by device',             widgets: ['sims:device-breakdown'] },
        { id: 'sims.dept',         label: 'Click rate by group',          widgets: ['sims:click-by-dept'] },
    ]},
    { group: 'Courses', items: [
        { id: 'courses.metrics',     label: 'Course metric strip',            widgets: ['courses:metric-courses-sent', 'courses:metric-completion-rate', 'courses:metric-avg-score', 'courses:metric-pass-mark-rate'] },
        { id: 'courses.by-course',   label: 'Top 10 incomplete courses',      widgets: ['courses:all-courses'] },
        { id: 'courses.outstanding', label: 'Lowest completion users - top 10', widgets: ['courses:outstanding-users-card'] },
    ]},
    { group: 'Risk', items: [
        { id: 'risk.methodology', label: 'Risk score methodology (narrative)' },
        { id: 'risk.factors',     label: 'Risk score factor breakdown', widgets: ['summary:factor-strip'] },
    ]},
    { group: 'Dark web monitoring', feature: 'darkWebMonitoring', items: [
        { id: 'dwm.summary',  label: 'DWM metric strip' },
        { id: 'dwm.trend',    label: 'Breach trend' },
        { id: 'dwm.by-email', label: 'Top 10 mentioned emails' },
    ]},
];

// Flat lookup so the order list and generators can resolve a section by id
// without walking the grouped structure each time.
export const SECTION_INDEX = (() => {
    const map = {};
    for (const g of COMPONENT_LIBRARY) for (const it of g.items) map[it.id] = { ...it, group: g.group, feature: g.feature };
    return map;
})();

// True when the tenancy is entitled to a block: its feature flag (if any) is on
// AND at least one of the widgets it is drawn from is still switched on for the
// tenancy. Entitlements are super-admin-owned, so a switched-off widget drops
// out of the export builder the same way it drops out of the report pages.
export function isSectionEntitled(meta) {
    if (!meta) return false;
    if (meta.feature && !FEATURES[meta.feature]) return false;
    if (!meta.widgets || meta.widgets.length === 0) return true;
    return meta.widgets.some((key) => {
        const [page, widgetId] = key.split(':');
        return isWidgetEntitled(page, widgetId);
    });
}

// -- Section resolvers -----------------------------------------------------
// Each returns a normalised block the preview + generators understand:
//   { kind: 'gauge',     value, band, sub }
//   { kind: 'metrics',   items: [{label, value, sub}] }
//   { kind: 'bars',      items: [{label, value, max}], unit }
//   { kind: 'table',     columns: [...], rows: [[...]], total }
//   { kind: 'narrative', paragraphs: [...] }  (paragraphs may be {h, body, action})
// `total` on a table is the full row count so a capped preview/PDF can say
// "showing 20 of N" and point the reader at the full download.
const deviceSplit = () => {
    let mobile = 0, desktop = 0;
    for (const e of SEND_LOG) { if (e.device === 'Mobile') mobile++; else desktop++; }
    return { mobile, desktop, total: mobile + desktop };
};


// -- Live-aggregation helpers ---------------------------------------------
// Each resolver prefers `live` (the real server aggregations the dashboard
// pages use, uncapped) and falls back to the client-hydrated modules when a
// slice is absent (History / Data tables re-runs, or a query still in flight).
const rnd = (n) => Math.round(Number(n) || 0);
const rate = (numer, denom) => (denom ? Math.round((numer / denom) * 100) : 0);
const uname = (u) => (`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '—');
const bandName = (score) => (score == null ? '—' : (bandFor(score)?.name || '—'));
const riskFromClick = (r) => (r >= 0.3 ? 'high' : r >= 0.15 ? 'med' : 'low');
const DIM_LABELS = { department: 'Department', adGroup: 'AD group', city: 'City', country: 'Country', location: 'Location', manager: 'Manager', jobTitle: 'Job title' };
const dimLabel = (t) => DIM_LABELS[t] || cap(String(t || ''));

// SIGNALS <-> server field mapping for the risk-factor table (same order as
// the five weighted signals). `org` = per-org mean field on the `risk` agg;
// `user` = per-user field on the `users` (riskUsers) agg.
const FACTOR_FIELDS = [
    { sig: 'fail-rate',     org: 'factorFailRate',     user: 'failRate' },
    { sig: 'repeat',        org: 'factorRepeat',       user: 'repeat' },
    { sig: 'stage-depth',   org: 'factorStageDepth',   user: 'stageDepth' },
    { sig: 'training-debt', org: 'factorTrainingDebt', user: 'trainingDebt' },
    { sig: 'course-lag',    org: 'factorCourseLag',    user: 'courseLag' },
];

// Bucket the daily `trends` rows into calendar months for a bars block (sum
// `field` per month). The server already windows the rows to the export period.
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (ym) => `${MONTH_ABBR[parseInt(ym.slice(5, 7), 10) - 1] || ym} ${ym.slice(2, 4)}`;
function bucketMonthly(trends, field) {
    const order = [];
    const map = {};
    for (const t of trends || []) {
        const key = String(t.day || '').slice(0, 7); // YYYY-MM
        if (key.length !== 7) continue;
        if (!(key in map)) { map[key] = 0; order.push(key); }
        map[key] += Number(t[field]) || 0;
    }
    return order.map((k) => ({ label: monthLabel(k), value: map[k] }));
}

// The export window as YYYY-MM-DD bounds: the caller's resolved dates when it
// has them, else counted back from today for a preset. Null = no bound. UTC
// throughout, the calendar the server counts against.
export function exportWindow({ period, startDate, endDate } = {}) {
    if (startDate && endDate) return { start: startDate, end: endDate };
    const days = PERIOD_DAYS[period];
    if (!days) return null;
    const end = new Date();
    const start = new Date(end);
    start.setUTCDate(end.getUTCDate() - days);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
}

const simTotals = (sims) => sims.reduce((a, s) => ({
    sent: a.sent + (s.sent || 0), clicks: a.clicks + (s.clicks || 0),
    reports: a.reports + (s.reports || 0), landings: a.landings + (s.landings || 0),
}), { sent: 0, clicks: 0, reports: 0, landings: 0 });

const SECTION_RESOLVERS = {
    // Benchmark anchors are gated behind the industryBenchmarking flag so the
    // export honours the same invariant as the on-screen snapshot card.
    'home.risk-dial': (opts = {}, live) => {
        if (live?.risk?.orgScore != null) {
            const score = live.risk.orgScore;
            const band = bandName(score);
            return {
                kind: 'gauge', value: score, band,
                sub: opts.benchmarkOn && live.benchmark
                    ? `Platform average ${live.benchmark.platformAvg} · top 25% ${live.benchmark.top25}`
                      + (live.benchmark.industryAvg != null ? ` · industry ${live.benchmark.industryAvg}` : '')
                    : `${band} band · scored 0-100, lower is better`,
            };
        }
        return {
            kind: 'gauge', value: ORG_SCORE, band: ORG_BAND?.name,
            sub: opts.benchmarkOn && BENCHMARK.platformAvg != null
                ? `Platform average ${BENCHMARK.platformAvg} · top 25% ${BENCHMARK.top25}`
                : `${ORG_BAND?.name} band · scored 0-100, lower is better`,
        };
    },
    'home.risk-trend': (opts = {}, live) => {
        // risk_trend ignores the date range server-side, so window it here.
        const w = exportWindow(opts);
        const pts = (Array.isArray(live?.riskTrend) ? live.riskTrend : [])
            .filter((p) => p.day && (!w || (p.day >= w.start && p.day <= w.end)));
        // No fallback: an empty window renders an empty chart, never demo data.
        return { kind: 'area', unit: 'score', max: 100, items: pts.map((p) => ({ label: `${p.day.slice(8, 10)} ${monthLabel(p.day)}`, value: p.score })) };
    },
    'home.top-risk': (opts = {}, live) => {
        if (Array.isArray(live?.riskUsers)) {
            const rows = [...live.riskUsers].sort((a, b) => b.score - a.score).slice(0, 10)
                .map((u) => [uname(u), u.dept || 'Unknown', u.score, u.band]);
            return { kind: 'table', columns: ['User', 'Department', 'Risk score', 'Band'], total: rows.length, rows };
        }
        const rows = TOP_RISK_USERS.slice(0, 10).map((u) => [u.name, u.dept, u.finalScore, u.band?.name]);
        return { kind: 'table', columns: ['User', 'Department', 'Risk score', 'Band'], total: rows.length, rows };
    },
    'home.metrics': (opts = {}, live) => {
        if (live?.summary?.periods) {
            const ps = live.summary.periods;
            const p = ps.find((x) => x.period === (opts.period || '12m')) || ps[ps.length - 1] || {};
            return { kind: 'metrics', items: [
                { label: 'Sim click rate', value: `${rate(p.clicks, p.sends)}%`, sub: `${num(p.clicks)} of ${num(p.sends)} sims` },
                { label: 'Course completion', value: `${rate(p.courseCompleted, p.courseSends)}%`, sub: `${num(p.courseCompleted)} of ${num(p.courseSends)}` },
                // Risk-scored users, not windowed to the period.
                { label: 'Engaged users', value: num(live.summary.activeUsers), sub: 'Not limited to this period' },
                { label: 'Report rate', value: `${rate(p.reports, p.sends)}%`, sub: 'Of sims delivered' },
            ] };
        }
        const d = PERIOD_DATA[opts.period] || PERIOD_DATA['6m'];
        return { kind: 'metrics', items: [
            { label: 'Sim click rate', value: `${d.simClickRate}%`, sub: d.simSub },
            { label: 'Course completion', value: `${d.courseCompletion}%`, sub: d.courseSub },
            { label: 'Engaged users', value: num(d.engagedUsers), sub: 'Not limited to this period' },
            { label: 'Report rate', value: `${d.reportRate}%`, sub: 'Of sims delivered' },
        ] };
    },
    'sims.metrics': (opts = {}, live) => {
        if (Array.isArray(live?.sims)) {
            const t = simTotals(live.sims);
            return { kind: 'metrics', items: [
                { label: 'Sims sent', value: num(t.sent), sub: `${live.sims.length} campaigns` },
                { label: 'Click rate', value: `${rate(t.clicks, t.sent)}%`, sub: `${num(t.clicks)} clicked` },
                { label: 'Report rate', value: `${rate(t.reports, t.sent)}%`, sub: 'Reported as phishing' },
                { label: '2nd stage rate', value: `${rate(t.landings, t.sent)}%`, sub: 'Clicked through to the landing page' },
            ] };
        }
        const d = PERIOD_DATA[opts.period] || PERIOD_DATA['6m'];
        const second = Array.isArray(d.funnelSegments) ? d.funnelSegments[2] : 0;
        return { kind: 'metrics', items: [
            { label: 'Sims sent', value: num(SEND_LOG.length), sub: `${SIMS.length} campaigns` },
            { label: 'Click rate', value: `${d.simClickRate}%`, sub: d.simSub },
            { label: 'Report rate', value: `${d.reportRate}%`, sub: 'Reported as phishing' },
            { label: '2nd stage rate', value: pct(d.funnelSent ? (second / d.funnelSent) * 100 : 0), sub: 'Clicked through to the landing page' },
        ] };
    },
    'sims.clicks': (opts = {}, live) => {
        if (Array.isArray(live?.trends)) return { kind: 'bars', unit: 'clicks', items: bucketMonthly(live.trends, 'clicks') };
        return { kind: 'bars', unit: 'clicks', items: (CLICKS_BY_MONTH[opts.period] || CLICKS_BY_MONTH['6m']).map((b) => ({ label: b.label, value: b.n })) };
    },
    'sims.funnel': (opts = {}, live) => {
        if (Array.isArray(live?.sims)) {
            const t = simTotals(live.sims);
            return { kind: 'bars', unit: 'events', items: [
                { label: 'Delivered', value: t.sent, max: t.sent },
                { label: 'No click', value: Math.max(0, t.sent - t.clicks), max: t.sent },
                { label: 'Clicked', value: t.clicks, max: t.sent },
                { label: '2nd stage', value: t.landings, max: t.sent },
                { label: 'Reported', value: t.reports, max: t.sent },
            ] };
        }
        const d = PERIOD_DATA[opts.period] || PERIOD_DATA['6m'];
        const [noClick, clicked, second, reported] = d.funnelSegments;
        return { kind: 'bars', unit: 'users', items: [
            { label: 'Delivered', value: d.funnelSent, max: d.funnelSent },
            { label: 'No click', value: noClick, max: d.funnelSent },
            { label: 'Clicked', value: clicked, max: d.funnelSent },
            { label: '2nd stage', value: second, max: d.funnelSent },
            { label: 'Reported', value: reported, max: d.funnelSent },
        ] };
    },
    'sims.by-sim': (opts = {}, live) => {
        if (Array.isArray(live?.sims)) {
            const rows = [...live.sims].filter((s) => s.sent > 0)
                .sort((a, b) => (b.clicks / b.sent) - (a.clicks / a.sent))
                .slice(0, 10)
                .map((s) => [s.simName, `${rate(s.clicks, s.sent)}%`, cap(riskFromClick(s.clicks / s.sent)), num(s.sent), `${rate(s.reports, s.sent)}%`]);
            return { kind: 'table', columns: ['Simulation', 'Click rate', 'Risk', 'Sent', 'Report rate'], total: rows.length, rows };
        }
        const rows = [...SIMS].sort((a, b) => parseInt(b.click) - parseInt(a.click)).slice(0, 10)
            .map((s) => [s.name, s.click, cap(s.lv), num(s.sent), `${s.reportRate}%`]);
        return { kind: 'table', columns: ['Simulation', 'Click rate', 'Risk', 'Sent', 'Report rate'], total: rows.length, rows };
    },
    'sims.device': (opts = {}, live) => {
        if (Array.isArray(live?.simDevices)) {
            const tot = live.simDevices.reduce((a, d) => a + (d.clicks || 0), 0);
            return { kind: 'bars', unit: 'clicks', items: live.simDevices.map((d) => ({ label: d.device, value: d.clicks, max: tot })) };
        }
        const d = deviceSplit();
        return { kind: 'bars', unit: 'clicks', items: [
            { label: 'Desktop', value: d.desktop, max: d.total },
            { label: 'Mobile', value: d.mobile, max: d.total },
        ] };
    },
    'sims.dept': (opts = {}, live) => {
        if (Array.isArray(live?.leagues)) {
            // Every populated dimension (department, city, location...)
            // appears, mirroring the league-table widget's selector options.
            const rows = live.leagues.filter((r) => r.name && r.name !== 'Unknown' && r.dimType !== 'adGroup')
                .sort((a, b) => (a.dimType === b.dimType ? b.users - a.users : String(a.dimType).localeCompare(String(b.dimType))))
                .map((r) => [r.name, dimLabel(r.dimType), `${rnd((r.failRate || 0) * 100)}%`, rnd(r.avgRisk), num(r.users)]);
            return { kind: 'table', columns: ['Group', 'Dimension', 'Click rate (last 6 months)', 'Risk score', 'Users'], total: rows.length, rows };
        }
        return { kind: 'table', columns: ['Group', 'Dimension', 'Click rate (last 6 months)', 'Risk score', 'Users'], total: DEPTS.length, rows: DEPTS.map((d) => [d.name, 'Department', `${d.click}%`, d.risk, num(d.users)]) };
    },
    'courses.metrics': (opts = {}, live) => {
        if (live?.courseDetail?.kpis) {
            const k = live.courseDetail.kpis;
            return { kind: 'metrics', items: [
                { label: 'Assignments', value: num(k.sent), sub: 'Sent this window' },
                { label: 'Completions', value: num(k.completed), sub: 'Marked complete' },
                { label: 'Completion rate', value: `${k.completionPct}%`, sub: 'Of assignments' },
                { label: 'Avg score', value: k.avgScore != null ? `${k.avgScore}%` : '-', sub: 'In this period' },
            ] };
        }
        const sent = COURSE_SEND_LOG.length;
        const completed = COURSE_SEND_LOG.filter((c) => c.dateCompleted).length;
        return { kind: 'metrics', items: [
            { label: 'Assignments', value: num(sent), sub: 'Sent this window' },
            { label: 'Completions', value: num(completed), sub: 'Marked complete' },
            { label: 'Completion rate', value: pct(sent === 0 ? 0 : (completed / sent) * 100), sub: 'Of assignments' },
            { label: 'Avg score', value: '-', sub: 'In this period' },
        ] };
    },
    // Worst-performing courses. Completion rate alone leaves a large tie on 0%
    // (39 of 52 courses on a typical tenancy), so the ten shown were an
    // arbitrary slice of that tie, every row reading 0 and 0%. Outstanding count
    // breaks the tie, which puts the biggest gaps first and gives the table a
    // column worth reading.
    'courses.by-course': (opts = {}, live) => {
        const worstFirst = (aRate, bRate, aOut, bOut) => (aRate - bRate) || (bOut - aOut);
        if (Array.isArray(live?.courses)) {
            const rows = live.courses.filter((c) => (c.sent || 0) > 0)
                .sort((a, b) => worstFirst(a.completed / a.sent, b.completed / b.sent,
                    (a.sent || 0) - (a.completed || 0), (b.sent || 0) - (b.completed || 0)))
                .slice(0, 10)
                .map((c) => [c.courseName, courseType(c.courseName), num(c.sent), num(c.completed),
                    num((c.sent || 0) - (c.completed || 0)), `${rate(c.completed, c.sent)}%`]);
            return { kind: 'table', columns: ['Course', 'Type', 'Assigned', 'Completed', 'Outstanding', 'Rate'], total: rows.length, rows };
        }
        const rows = [...COURSES]
            .sort((a, b) => worstFirst(a.pct, b.pct, a.sent - a.completed, b.sent - b.completed))
            .slice(0, 10)
            .map((c) => [c.name, c.type, num(c.sent), num(c.completed), num(c.sent - c.completed), `${c.pct}%`]);
        return { kind: 'table', columns: ['Course', 'Type', 'Assigned', 'Completed', 'Outstanding', 'Rate'], total: rows.length, rows };
    },
    // Same tie problem as the course table: plenty of users sit on 0%, so the
    // outstanding count decides which ten are worth naming.
    'courses.outstanding': (opts = {}, live) => {
        const worstFirst = (aRate, bRate, aOut, bOut) => (aRate - bRate) || (bOut - aOut);
        if (Array.isArray(live?.courseDetail?.outstandingUsers)) {
            const rows = [...live.courseDetail.outstandingUsers]
                .map((u) => ({ ...u, done: (u.sent || 0) - (u.outstanding || 0) }))
                .filter((u) => (u.sent || 0) > 0)
                .sort((a, b) => worstFirst(a.done / a.sent, b.done / b.sent, a.outstanding || 0, b.outstanding || 0))
                .slice(0, 10)
                .map((u) => [uname(u), u.dept || 'Unknown', num(u.sent), num(u.done), num(u.outstanding || 0), `${rate(u.done, u.sent)}%`]);
            return { kind: 'table', columns: ['User', 'Department', 'Assigned', 'Completed', 'Outstanding', 'Rate'], total: rows.length, rows };
        }
        const rows = COURSE_USER_COMPLETIONS.filter((u) => u.pct < 100)
            .sort((a, b) => worstFirst(a.pct, b.pct, a.sent - a.completed, b.sent - b.completed))
            .slice(0, 10);
        return { kind: 'table', columns: ['User', 'Department', 'Assigned', 'Completed', 'Outstanding', 'Rate'], total: rows.length,
            rows: rows.map((u) => [`${u.firstName} ${u.lastName}`, u.dept, num(u.sent), num(u.completed), num(u.sent - u.completed), `${u.pct}%`]) };
    },
    'risk.methodology': (opts = {}, live) => {
        const score = live?.risk?.orgScore != null ? live.risk.orgScore : ORG_SCORE;
        const band = live?.risk?.orgScore != null ? bandName(score) : ORG_BAND?.name;
        return { kind: 'narrative', paragraphs: [
            { h: 'How the risk score is built', body: `Five weighted behavioural signals over a rolling 6-month window produce a 0-100 score. Lower is better. The current org score is ${score} (${band}).` },
            ...SIGNALS.map((s) => ({ h: `${s.label} (${s.weight}%)`, body: `${cap(s.meaning)}. ${s.description}` })),
        ] };
    },
    'risk.factors': (opts = {}, live) => {
        // Contribution is expressed in points of the org score: each factor's
        // weighted share of the total, scaled so the column sums to the score.
        // (The raw weight x mean product understated everything by roughly an
        // order of magnitude because it ignored the model's sample-size easing.)
        if (live?.risk) {
            const parts = FACTOR_FIELDS.map(({ sig, org }) => {
                const sg = SIGNALS.find((x) => x.id === sig) || {};
                return { sg, mean: Number(live.risk[org]) || 0, w: sg.weight || 0 };
            });
            const totalWeighted = parts.reduce((a, f) => a + f.w * f.mean, 0);
            const orgScore = Number(live.risk.orgScore) || 0;
            const rows = parts.map(({ sg, mean, w }) => {
                const meanLabel = sg.anchor?.format === 'days' ? `${(mean * 30).toFixed(1)}d` : pct(mean * 100);
                const points = totalWeighted > 0 ? (orgScore * (w * mean)) / totalWeighted : 0;
                return [sg.label, `${w}%`, meanLabel, `${points.toFixed(1)} pts`];
            });
            return { kind: 'table', columns: ['Factor', 'Weight', 'Org mean', 'Score contribution'], total: rows.length, rows };
        }
        const totalW = ORG_FACTOR_STATS.reduce((a, f) => a + f.weight * f.componentMean, 0);
        return { kind: 'table', columns: ['Factor', 'Weight', 'Org mean', 'Score contribution'], total: ORG_FACTOR_STATS.length,
            rows: ORG_FACTOR_STATS.map((f) => [f.label, `${f.weight}%`, pct(f.componentMean * 100), `${(totalW > 0 ? (ORG_SCORE * f.weight * f.componentMean) / totalW : 0).toFixed(1)} pts`]) };
    },
    'dwm.summary': (opts = {}, live) => {
        if (live?.dwmStats) {
            const d = live.dwmStats;
            return { kind: 'metrics', items: [
                { label: 'Total breaches', value: num(d.allTimeRows ?? 0), sub: 'All time, all sources' },
                { label: 'Breaches in period', value: num(d.totalMentions ?? 0), sub: 'Within the export period' },
                { label: 'Emails breached', value: num(d.distinctEmails ?? 0), sub: 'Within the export period' },
                { label: 'Credential leaks', value: num(d.credMentions ?? 0), sub: 'Plaintext password exposed' },
            ] };
        }
        return { kind: 'metrics', items: [
            { label: 'Total breaches', value: num(DWM_SUMMARY.totalBreaches), sub: 'All time, all sources' },
            { label: 'Breaches in period', value: '\u2014', sub: 'Within the export period' },
            { label: 'Emails breached', value: num(DWM_SUMMARY.emailsBreached), sub: `of ${num(DWM_SUMMARY.emailsScanned)} scanned` },
            { label: 'Credential leaks', value: num(DWM_SUMMARY.credentialBreaches), sub: 'Email + password' },
        ] };
    },
    'dwm.trend': (opts = {}, live) => {
        if (Array.isArray(live?.dwmTrend) && live.dwmTrend.length) {
            return { kind: 'area', unit: 'breaches', items: live.dwmTrend.map((m) => ({ label: m.label, value: m.value })) };
        }
        return { kind: 'area', unit: 'breaches', items: DWM_TREND.slice(-12).map((m) => ({ label: m.label, value: m.creds + m.emailOnly })) };
    },
    'dwm.by-email': (opts = {}, live) => {
        if (Array.isArray(live?.dwmByEmail)) {
            const rows = live.dwmByEmail.slice(0, 10).map((e) => [e.email, num(e.mentions), num(e.withCreds), e.latest]);
            return { kind: 'table', columns: ['Email', 'Mentions', 'With creds', 'Latest'], total: rows.length, rows };
        }
        const rows = DWM_BY_EMAIL.slice(0, 10).map((e) => [e.email, num(e.mentions), num(e.withCreds), e.latest]);
        return { kind: 'table', columns: ['Email', 'Mentions', 'With creds', 'Latest'], total: rows.length, rows };
    },
    // Mirrors the Risk Summary card: the benchmark gap, the heaviest score
    // drivers from the live factor means, and the critical-user callout.
    'text.implication': (opts = {}, live) => {
        const score = (live?.risk?.orgScore ?? ORG_SCORE) ?? 0;
        const critical = Array.isArray(live?.riskUsers)
            ? live.riskUsers.filter((u) => u.band === 'Critical').length
            : TOP_RISK_USERS.filter((u) => u.band?.name === 'Critical').length;
        const active = live?.risk?.scoredUsers ?? ACTIVE_USERS;
        const paragraphs = [];
        // Peer comparison only when a real anchor exists - never against null.
        const bench = live?.benchmark || BENCHMARK;
        const peerAvg = bench.industryAvg ?? bench.platformAvg;
        const peerLabel = bench.industryAvg != null ? 'industry' : 'platform';
        if (opts.benchmarkOn && peerAvg != null) {
            const gap = score - peerAvg;
            paragraphs.push({ h: null, body: gap > 0
                ? `You are more of a risk than the average peer: your score sits ${Math.abs(gap)} points above the ${peerLabel} average of ${peerAvg}.`
                : `You are outperforming peers: your score sits ${Math.abs(gap)} points below the ${peerLabel} average of ${peerAvg}.` });
        }
        if (live?.risk) {
            const parts = FACTOR_FIELDS
                .map(({ sig, org }) => { const sg = SIGNALS.find((x) => x.id === sig) || {}; return { sg, weighted: (sg.weight || 0) * (Number(live.risk[org]) || 0) }; })
                .sort((a, b) => b.weighted - a.weighted);
            if (parts[0]?.weighted > 0) {
                const top = parts[0].sg, second = parts[1]?.weighted > 0 ? parts[1].sg : null;
                paragraphs.push({ h: null, body: `${top.label} is the heaviest driver of your risk score at ${top.weight}% of the weighting${second ? `, with ${second.label.toLowerCase()} the next largest contributor` : ''}. Reducing it moves the score fastest.` });
            }
        }
        paragraphs.push({ h: null, body: `${num(critical)} users are in the Critical band out of ${num(active)} active. Targeting them moves the org score faster than broad-based training.` });
        return { kind: 'narrative', paragraphs };
    },
};

// `opts` (e.g. { benchmarkOn }) lets a resolver respect tenancy feature flags
// without each one reaching into the flag store itself.
export function resolveSection(id, opts = {}, live = null) {
    const fn = SECTION_RESOLVERS[id];
    return fn ? fn(opts, live) : { kind: 'narrative', paragraphs: [{ h: null, body: 'No preview available for this section.' }] };
}

// Build the full ordered, resolved section list the preview + generators
// consume. `withDrill` is preserved so generators can append row detail; `opts`
// flows through to the resolvers (feature-flag aware).
export function assembleSections(selectedIds, drillIds, opts = {}, live = null) {
    const drill = drillIds instanceof Set ? drillIds : new Set(drillIds || []);
    return selectedIds
        .map((id) => SECTION_INDEX[id])
        .filter(Boolean)
        .map((it) => ({ ...it, withDrill: drill.has(it.id), block: resolveSection(it.id, opts, live) }));
}

// -- Provided templates ----------------------------------------------------
// Ready-made layouts. `sections` is the ordered id list; `drill` the subset
// that should default to including row-level detail.
export const PROVIDED_TEMPLATES = [
    {
        id: 'board-pack', name: 'Board pack', format: 'PDF',
        title: 'Board security report',
        description: 'One-page narrative for non-technical board members.',
        sections: ['home.risk-dial', 'home.risk-trend', 'text.implication', 'home.top-risk'],
        drill: [],
    },
    {
        id: 'exec-review', name: 'Exec review', format: 'PowerPoint',
        title: 'Executive security review',
        description: 'Slide-per-theme deck for the leadership meeting.',
        sections: ['home.metrics', 'home.risk-dial', 'sims.funnel', 'sims.by-sim', 'courses.metrics'],
        drill: [],
    },
    {
        id: 'monthly-ops', name: 'Monthly ops review', format: 'Excel',
        title: 'Monthly operations review',
        description: 'Operational detail for the security team.',
        sections: ['sims.metrics', 'sims.clicks', 'sims.by-sim', 'sims.dept', 'courses.metrics', 'courses.by-course', 'home.top-risk'],
        drill: [],
    },
    {
        id: 'compliance', name: 'Compliance evidence', format: 'PDF',
        title: 'Compliance evidence pack',
        description: 'Training completion and methodology for auditors.',
        sections: ['courses.metrics', 'courses.by-course', 'courses.outstanding', 'risk.methodology', 'risk.factors'],
        drill: [],
    },
    {
        id: 'risk-deep-dive', name: 'Risk deep-dive', format: 'Excel',
        title: 'Risk score deep-dive',
        description: 'Full factor breakdown and highest-risk users.',
        sections: ['home.risk-dial', 'risk.factors', 'risk.methodology', 'home.top-risk'],
        drill: [],
    },
];

// -- Custom template persistence -------------------------------------------
// Server-side since the ReportLayouts store gained SK EXPORT_TEMPLATES: the
// builder and scheduled-exports pages read/write via reportLayout.service
// (GET/PUT /dashboard/export-templates/{tenancyId}), so templates are
// per-tenancy, shared across browsers, and never leak between tenancies.

// -- Large tables ----------------------------------------------------------
// Full row-level datasets the customer can download on their own, in any of
// the four formats, without composing a document. Columns mirror the report
// views; `getRows` returns plain arrays aligned to `columns`.
// Aligned to their main-page counterparts (same columns, same derivations -
// the sim/course history tables share the pages' helper functions). Tables
// whose main view is server-fed (`users`, `dwm-by-email`) accept live rows
// from the panel via getRows(liveRows) and fall back to client data.
function liveEvents(records, mapper) {
    return records.map(mapper).filter((e) => e.dateSent).sort((a, b) => (a.dateSent < b.dateSent ? 1 : -1));
}

const ROSTER_REQUEST = {
    metric: 'users', aggregation: 'user_roster', limit: 20000,
    engagement: 'all', includeDisabled: true,
    sort: { field: 'riskScore', direction: 'desc' },
};

// Every table is fetched from the server for the chosen window ({ startDate,
// endDate }). The browser's own logs only hold 13 months, so falling back to
// them silently cut long histories short (BD-1124).
const LIVE_TABLE_SOURCES = {
    users:          { request: (w) => ({ ...ROSTER_REQUEST, ...w }), pick: (d) => d?.roster },
    'high-risk':    { request: (w) => ({ ...ROSTER_REQUEST, ...w }), pick: (d) => d?.roster },
    'send-history': { request: (w) => ({ metric: 'simulations', aggregation: 'events', ...w }), pick: (d) => d?.records, lazy: true },
    'course-sends': { request: (w) => ({ metric: 'courses', aggregation: 'events', ...w }), pick: (d) => d?.records, lazy: true },
    'all-sims':     { request: (w) => ({ metric: 'simulations', aggregation: 'sims', ...w }), pick: (d) => d?.sims },
};

// RecordCap in the server's DashboardRepository: events and the roster stop
// at this many rows, so a table that comes back this size was cut short.
export const TABLE_ROW_CAP = 20000;

export function largeTableRequest(id, scope, window) {
    const src = LIVE_TABLE_SOURCES[id];
    return src ? { ...src.request(window), scope } : null;
}

export function largeTableLiveRows(id, data) {
    const rows = LIVE_TABLE_SOURCES[id]?.pick(data);
    return Array.isArray(rows) ? rows : null;
}

export function isLazyLargeTable(id) {
    return !!LIVE_TABLE_SOURCES[id]?.lazy;
}

export const LARGE_TABLES = [
    {
        id: 'users', label: 'All users', source: 'Users',
        description: 'The main Users table with every field included.',
        columns: ['Name', 'Email', 'Department', 'Location', 'City', 'Country', 'State', 'Company', 'Group', 'Manager',
                  'Sims sent', 'Click rate', '2nd Stage Clicked %', 'Reported %', 'Courses sent', 'Completion', 'Avg score', 'Reminders', 'Risk score', 'Band', 'Status'],
        getRows: (live) => (live || []).map((r) => [
            `${r.firstName || ''} ${r.lastName || ''}`.trim() || r.email, r.email,
            r.dept || '-', r.location || '-', r.city || '-', r.country || '-', r.state || '-', r.company || '-',
            Array.isArray(r.adGroups) ? r.adGroups.join(', ') : (r.adGroup || '-'), r.manager || '-',
            r.simsSent ?? '-', r.clickRate != null ? `${r.clickRate}%` : '-',
            r.secondStagePct != null ? `${r.secondStagePct}%` : '-', r.reportedPct != null ? `${r.reportedPct}%` : '-',
            r.coursesSent ?? '-', `${r.completionPct ?? 0}%`, r.avgScore != null ? `${r.avgScore}%` : '-',
            r.reminders ?? '-',
            r.riskScore ?? 'Unscored', r.band || '-', r.enabled === false ? 'Disabled' : 'Active',
        ]),
    },
    {
        id: 'send-history', label: 'Simulation send history', source: 'Simulations',
        description: 'The main Send History table: every stage timestamp per recipient.',
        columns: ['Name', 'Email', 'Simulation', 'Status', 'Sent', 'Clicked', '2nd Stage Clicked', 'Reported',
                  'Department', 'Location', 'City', 'Country', 'State', 'Company', 'Group', 'Device'],
        getRows: (live) => liveEvents(live || [], mapSimEvent).map((e) => [
            `${e.firstName || ''} ${e.lastName || ''}`.trim() || e.email, e.email, e.simName,
            STATUS_META[e.status]?.label || e.status,
            fmtLocal(e.sentAt), fmtLocal(e.clickedAt), fmtLocal(e.landedAt), fmtLocal(e.reportedAt),
            e.dept || '-', e.location || '-', e.city || '-', e.country || '-', e.state || '-', e.company || '-',
            Array.isArray(e.adGroups) ? e.adGroups.join(', ') : (e.adGroup || '-'), e.device || '-',
        ]),
    },
    {
        id: 'course-sends', label: 'Course send history', source: 'Courses',
        description: 'The main Course Send History table with status, outcome and score.',
        columns: ['Name', 'Email', 'Course', 'Status', 'Outcome', 'Score', 'Sent', 'Completed', 'Reminders',
                  'Department', 'Location', 'City', 'Country', 'State', 'Company', 'Group'],
        getRows: (live) => liveEvents(live || [], mapCourseEvent).map((e) => {
            const score = courseScore(e);
            return [
                `${e.firstName || ''} ${e.lastName || ''}`.trim() || e.email, e.email, e.courseName,
                courseStatus(e), courseOutcome(e) || '', score == null ? '' : `${score}%`,
                fmtLocal(e.sentAt), fmtLocal(e.completedAt), e.reminders ?? 0,
                e.dept || '-', e.location || '-', e.city || '-', e.country || '-', e.state || '-', e.company || '-',
                Array.isArray(e.adGroups) ? e.adGroups.join(', ') : (e.adGroup || '-'),
            ];
        }),
    },
    {
        id: 'high-risk', label: 'High click users', source: 'Users',
        description: 'The main High Click Users table: engagement funnel per user.',
        columns: ['Name', 'Email', 'Risk score', 'Sent', 'Clicked', '2nd stage', 'Reported', 'Click rate', 'Report rate'],
        getRows: (live) => {
            const pct = (n, sent) => (sent ? Math.round((n / sent) * 100) : 0);
            return (live || []).map((r) => {
                const sent = r.simsSent || 0;
                const clicked = r.simsClicked || 0;
                const reported = r.simsReported || 0;
                return { r, sent, clicked, reported, clickRate: pct(clicked, sent), reportRate: pct(reported, sent) };
            })
                .sort((a, b) => b.clicked - a.clicked || b.clickRate - a.clickRate)
                .map(({ r, sent, clicked, reported, clickRate, reportRate }) => [
                    `${r.firstName || ''} ${r.lastName || ''}`.trim() || r.email, r.email, r.riskScore ?? 'Unscored',
                    sent, clicked, r.simsLanded || 0, reported, `${clickRate}%`, `${reportRate}%`,
                ]);
        },
    },
    {
        id: 'all-sims', label: 'All simulations', source: 'Simulations',
        description: 'Every campaign with click rate, difficulty and flags.',
        columns: ['Simulation', 'Level', 'Click rate', 'Sent', 'Report rate', 'Difficulty', 'Flagged'],
        getRows: (live) => simStatsFromAgg(live || []).map((s) => [s.name, cap(s.lv), s.click, s.sent, `${s.reportRate}%`, `${s.diff}/3`, s.flag ? 'Yes' : 'No']),
    },
    {
        id: 'dwm-by-email', label: 'Dark web mentions by email', source: 'Dark web monitoring', feature: 'darkWebMonitoring',
        description: 'The DWM Most-mentioned table, across every breached email.',
        columns: ['Email', 'Name', 'Department', 'Mentions', 'With creds', 'Sources', 'Latest'],
        getRows: (live) => (live || []).map((e) => [e.email, e.name || '-', e.dept || '-', e.mentions, e.withCreds, e.sources ?? '-', e.latest || '-']),
    },
];

// -- Schedule persistence --------------------------------------------------
// Cadence is capped at monthly: weekly / daily are deliberately not offered
// so an export can fire at most once a month. Quarterly and annually sit
// below that ceiling.
export const CADENCES = [
    { id: 'monthly',   label: 'Monthly',   months: 1, note: 'Most frequent allowed' },
    { id: 'quarterly', label: 'Quarterly', months: 3 },
    { id: 'annually',  label: 'Annually',  months: 12 },
];

// The schedule anchors on an explicit first-send date and recurs from it by
// the cadence interval (the old day-of-month picker could only place the
// first quarterly/annual send inside the next month). Returns YYYY-MM-DD.
export function defaultFirstSend(from = new Date()) {
    // First of next month - a sensible starting suggestion.
    return new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 1)).toISOString().slice(0, 10);
}

// Schedules are stored as ISO (yyyy-mm-dd) because that's what a date input and
// any API want, but ISO is not how anyone here reads a date - every place we
// SHOW one renders it dd-mm-yyyy so the copy matches the picker beside it.
export function dmy(iso) {
    if (!iso) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso));
    return m ? `${m[3]}-${m[2]}-${m[1]}` : String(iso);
}

export function nextSendAfter(firstSend, cadence, from = new Date()) {
    const months = (CADENCES.find((c) => c.id === cadence) || CADENCES[0]).months;
    const next = new Date(`${firstSend}T00:00:00Z`);
    if (Number.isNaN(next.getTime())) return defaultFirstSend(from);
    while (next < from) next.setUTCMonth(next.getUTCMonth() + months);
    return next.toISOString().slice(0, 10);
}

// -- Export history --------------------------------------------------------
// Server-side per tenancy since the ReportLayouts store gained SK
// EXPORT_HISTORY: pages read/append/clear via reportLayout.service, so the
// log is shared across the tenancy's admins and devices (server-capped at
// the most recent 200 entries).

// Composed documents. CSV produces a .zip holding one real CSV per section
// (a single flat file cannot carry many differently-shaped widgets); the
// Data tables page still exports single-table CSVs directly.
export const FORMATS = ['PDF', 'PowerPoint', 'Excel', 'CSV'];
export const TABLE_FORMATS = ['CSV', 'Excel', 'JSON', 'PDF'];

// Selectable data periods for a composed export. Ids match the dashboard API's
// period values (and the PERIOD_DATA / CLICKS_BY_MONTH fallback keys), so the
// builder's queries and the client fallbacks stay on the same window.
export const EXPORT_PERIODS = [
    { id: '30d', label: '30 days' },
    { id: '90d', label: '90 days' },
    { id: '6m',  label: '6 months' },
    { id: '12m', label: '12 months' },
    { id: 'custom', label: 'Custom' },
];
export const periodLabel = (id) => (EXPORT_PERIODS.find((p) => p.id === id) || {}).label || id;

// What the report says about its own window. Presets read "Last 90 days"; a
// custom range names the actual dates, because "Custom" on a cover page tells
// the reader nothing.
const DMY = (iso) => {
    if (!iso) return '';
    const d = new Date(`${iso}T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
// Data tables can also pull everything on record ('all', see tableDateRange).
export const TABLE_PERIODS = [{ id: 'all', label: 'All time' }, ...EXPORT_PERIODS];

export function exportPeriodLabel(period, startDate, endDate) {
    if (period === 'all') return 'All time';
    if (period === 'custom') {
        if (startDate && endDate) return `${DMY(startDate)} - ${DMY(endDate)}`;
        return 'Custom range';
    }
    return `Last ${periodLabel(period)}`;
}

// Dimensions the export can be filtered by: everything the main pages offer
// except User, Simulation and Course, which scope a single record rather than
// a population and don't belong on a whole-report filter.
export const EXPORT_FILTER_DIMS = [
    { id: 'department', label: 'Department' },
    { id: 'location',   label: 'Location' },
    { id: 'city',       label: 'City' },
    { id: 'country',    label: 'Country' },
    { id: 'company',    label: 'Company' },
    { id: 'state',      label: 'State' },
    // tenancy values are numeric child-tenancy ids (backend scope.tenancy is
    // number[]); the export builder only offers this dim when the tenancy has
    // descendants and maps ids -> names for display.
    { id: 'tenancy',    label: 'Tenancy' },
];

// One-line description of an active filter set, for the document header and the
// history entry - a filtered report that doesn't say so is misleading.
// tenancyLabels (optional) maps tenancy id -> name so the tenancy dim reads as
// names rather than raw ids.
export function scopeSummary(scope, tenancyLabels = null) {
    const bits = EXPORT_FILTER_DIMS
        .map(({ id, label }) => {
            const v = scope && scope[id];
            const vals = Array.isArray(v) ? v : (v ? [v] : []);
            if (!vals.length) return null;
            const shown = (id === 'tenancy' && tenancyLabels)
                ? vals.map((x) => tenancyLabels[x] || x)
                : vals;
            return `${label}: ${shown.join(', ')}`;
        })
        .filter(Boolean);
    if (scope && scope.excludeDisabled) bits.push('Disabled users excluded');
    return bits.join(' · ');
}

// Shared TextField styling for the export builder. MUI's default size="small"
// padding (8.5px) plus a 12px font reads thin next to the portal's own inputs;
// this bumps the box to a comfortable ~35px, rounds to the portal's 6px radius,
// fills with var(--surface2) and matches the teal focus ring. Covers text,
// email, number and multiline fields, so individual fields drop their per-field
// fontSize overrides. All values resolve inside .rbx-root.
export const INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        fontFamily: 'Inter, sans-serif',
        fontSize: '12px',
        backgroundColor: 'var(--surface2)',
    },
    '& .MuiOutlinedInput-input': {
        fontSize: '12px',
        lineHeight: '1.4',
        padding: '9px 12px',
        minHeight: '18px',
        boxSizing: 'border-box',
    },
    '& .MuiOutlinedInput-root.MuiInputBase-multiline': { padding: '9px 12px' },
    '& .MuiOutlinedInput-root.MuiInputBase-multiline .MuiOutlinedInput-input': { padding: 0 },
    '& input[type=number]': { MozAppearance: 'textfield' },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
        WebkitAppearance: 'none', margin: 0,
    },
    '& .MuiInputLabel-root': { fontSize: '12px', color: 'var(--muted)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'var(--teal)' },
    // Resting border matches the portal's .rep-* inputs (var(--border2)) rather
    // than MUI's default; focus brightens to teal.
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--border2)', borderWidth: '1px',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--teal)', borderWidth: '1px',
    },
};
